import { Collector, CollectorAction, CollectorBillingPeriodData } from '@/domain/collectors/collector'
import { ApiResponse, PaginatedResponse } from '@/domain'
import { BillingType } from '@/domain/invoices/invoice'
import { CollectorPaymentsParams, collectorsApi } from '@/rest/modules/collectors-api'
import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'

export const collectorKeys = {
  all: ['collectors'] as const,
  lists: () => [...collectorKeys.all, 'list'] as const,
  collectors: (filters: { status?: string }) => [...collectorKeys.lists(), { filters }] as const,
  details: () => [...collectorKeys.all, 'detail'] as const,
  detail: (id: string) => [...collectorKeys.details(), id] as const,
  billingPeriods: (params: CollectorPaymentsParams) =>
    [...collectorKeys.all, 'billing-periods', params] as const,
}

export const useCollectors = (
  filters?: { status?: string; }
): UseQueryResult<Collector[], unknown> => {
  return useQuery({
    queryKey: collectorKeys.collectors(filters || {}),
    queryFn: () => collectorsApi.getAllCollectors(filters),
    staleTime: 0,
  })
}

export const useCollector = (id: string): UseQueryResult<Collector, unknown> => {
  return useQuery({
    queryKey: collectorKeys.detail(id),
    queryFn: () => collectorsApi.getCollector(id),
    enabled: !!id,
  })
}

export const useCollectorPayments = (
  params: CollectorPaymentsParams
): UseQueryResult<PaginatedResponse<CollectorBillingPeriodData>, unknown> => {
  return useQuery({
    queryKey: collectorKeys.billingPeriods(params),
    queryFn: () => collectorsApi.getCollectorPayments(params),
    enabled: !!params.periodStart,
    staleTime: 0,
  })
}

export const useModifyPaymentsByCollector = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ collectorId, paymentIds, operation, billingType }: { collectorId: string; paymentIds: string[]; operation: CollectorAction; billingType: BillingType }) =>
      collectorsApi.modifyPaymentsByCollector(collectorId, paymentIds, operation, billingType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: collectorKeys.all })
      toast({
        title: 'Éxito',
        description: 'Períodos asignados correctamente al cobrador',
        variant: 'default',
      })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al asignar períodos al cobrador',
        variant: 'destructive',
      })
    },
  })
}

export const useCreateCollector = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: Partial<Collector>) => collectorsApi.createCollector(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: collectorKeys.all })
      toast({
        title: 'Éxito',
        description: 'Cobrador creado correctamente',
        variant: 'default',
      })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al crear cobrador',
        variant: 'destructive',
      })
    },
  })
}

export const useUpdateCollector = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Collector> }) => collectorsApi.updateCollector(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: collectorKeys.all })
      toast({
        title: 'Éxito',
        description: 'Cobrador actualizado correctamente',
        variant: 'default',
      })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al actualizar cobrador',
        variant: 'destructive',
      })
    },
  })
}

export const useDeleteCollector = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (id: string) => collectorsApi.deleteCollector(id).then(() => id),
    onSuccess: (deletedId) => {
      queryClient.removeQueries({ queryKey: collectorKeys.detail(deletedId) })
      queryClient.invalidateQueries({ queryKey: collectorKeys.all })
      toast({
        title: 'Éxito',
        description: 'Cobrador eliminado correctamente',
        variant: 'default',
      })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al eliminar cobrador',
        variant: 'destructive',
      })
    },
  })
}